import { KnowledgeBaseLevel } from '@/types/knowledgeBase'
import type { AddressDto } from '@/types/company'
import type { KnowledgeBaseCategoryConfigDto, KnowledgeBaseCategoryStatusDto } from '@/types/knowledgeBase'

/**
 * Whose entry a level shows, and what it may hold of its own, in the current app's knowledge base (ORB-1027).
 */

/** The company and the workspace the page is showing, by name, so a row can say whose entry it is. */
export interface LevelNames {
  company: string | null
  workspace: string | null
}

/**
 * Whether a level may hold its own entry of a category. The company and every workspace may hold one of any category;
 * a campaign only of those its config lets campaigns change. Mirrors KnowledgeBaseValidator, which refuses only a
 * campaign entry of a category that is not CampaignOverridable.
 */
export function levelMayHoldEntry(level: KnowledgeBaseLevel, config: Pick<KnowledgeBaseCategoryConfigDto, 'CampaignOverridable'>): boolean {
  return level !== KnowledgeBaseLevel.Campaign || config.CampaignOverridable
}

/** The level's own name in a sentence: the workspace by its name, a campaign as this campaign. */
export function levelOwner(level: KnowledgeBaseLevel, names: LevelNames): string {
  if (level === KnowledgeBaseLevel.Campaign) return 'this campaign'
  return level === KnowledgeBaseLevel.Workspace ? names.workspace ?? 'this workspace' : names.company ?? 'the company'
}

/** Whose entry a level shows when it is not its own: the workspace's, for a campaign, or else the company's. */
export function inheritedOwner(status: Pick<KnowledgeBaseCategoryStatusDto, 'EffectiveLevel'>, names: LevelNames): string {
  return status.EffectiveLevel === KnowledgeBaseLevel.Workspace ? names.workspace ?? 'the workspace' : names.company ?? 'the company'
}

/**
 * Whose entry a row shows, in words, under its state. Nothing at the company, whose entries are all its own, and nothing
 * on an entry no level has.
 *
 * ⚑ Its own whenever the level holds an entry of its own, empty or not: an empty one is the level saying it has nothing
 * to add, and reading it as inherited would hide both the entry and the way to delete it.
 */
export function entryOwnerNote(
  status: Pick<KnowledgeBaseCategoryStatusDto, 'HasEntryAtThisLevel' | 'IsInherited' | 'EffectiveLevel'>,
  level: KnowledgeBaseLevel,
  names: LevelNames,
): string | null {
  if (level === KnowledgeBaseLevel.Company) return null
  if (status.HasEntryAtThisLevel) return `${capitalized(levelOwner(level, names))}'s own`
  return status.IsInherited ? `From ${inheritedOwner(status, names)}` : null
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// "3011 AB Rotterdam": continental Europe writes the postal code before the city.
const POSTAL_CODE_BEFORE_CITY = new Set(['DE', 'FR', 'NL', 'ES', 'IT', 'BE', 'AT', 'CH', 'SE', 'DK', 'NO', 'FI', 'PL', 'CZ', 'PT', 'GR'])
// "New York, NY 10012" and "Manchester M3 3HN": the other supported countries write it after the city.
const POSTAL_CODE_AFTER_CITY = new Set(['US', 'CA', 'GB', 'IE', 'AU', 'NZ', 'SG', 'IN'])

function joinPresent(separator: string, ...parts: (string | null | undefined)[]): string {
  return parts.filter((part): part is string => !!part && part.trim() !== '').map(part => part.trim()).join(separator)
}

/**
 * The postal address on one line, in the order its country's post writes one, as every email footer prints it. Mirrors
 * PostalAddressFormatter.FormatSingleLine; null for a country the platform has no order for, which the API refuses.
 */
export function postalAddressLine(address: AddressDto, countryName: string): string | null {
  const code = address.CountryCode.toUpperCase()
  let locality: string
  if (POSTAL_CODE_BEFORE_CITY.has(code)) {
    locality = joinPresent(', ', joinPresent(' ', address.PostalCode, address.City), address.RegionOrState)
  } else if (POSTAL_CODE_AFTER_CITY.has(code)) {
    locality = joinPresent(' ', joinPresent(', ', address.City, address.RegionOrState), address.PostalCode)
  } else {
    return null
  }
  return joinPresent(', ', address.StreetAddress, address.StreetAddressLineTwo, locality, countryName)
}
